import { PaymentIntentStatus } from "@app/payment/constants/PaymentIntentStatus";
import { PaymentProviderClient } from "@app/payment/constants/PaymentProviderClient";
import { PaymentSetupIntentStatus } from "@app/payment/constants/PaymentSetupIntentStatus";
import { stripeCheckoutReturnParams } from "@app/stripe/utils/stripeUtils";
import { UtilsUrl } from "@hotelchamp/common";

export const isPaymentAccepted = (status: PaymentIntentStatus | PaymentSetupIntentStatus) =>
    [PaymentIntentStatus.RequiresCapture, PaymentIntentStatus.Succeeded, PaymentSetupIntentStatus.Succeeded].includes(status);

export const isPaymentPending = (status: PaymentIntentStatus | PaymentSetupIntentStatus) =>
    [
        PaymentIntentStatus.RequiresAction,
        PaymentIntentStatus.Processing,
        PaymentSetupIntentStatus.RequiresAction,
        PaymentSetupIntentStatus.Processing,
    ].includes(status);

export const doesRequiresAction = (status: PaymentIntentStatus | PaymentSetupIntentStatus) =>
    [PaymentIntentStatus.RequiresAction, PaymentSetupIntentStatus.RequiresAction].includes(status);

export const isPaymentCancelled = (status: PaymentIntentStatus | PaymentSetupIntentStatus) =>
    [PaymentIntentStatus.Canceled, PaymentSetupIntentStatus.Canceled].includes(status);

export const isPaymentIdle = (status: PaymentIntentStatus | PaymentSetupIntentStatus) =>
    [PaymentIntentStatus.RequiresPaymentMethod, PaymentSetupIntentStatus.RequiresPaymentMethod].includes(status);

/**
 * Whether the payment status can be confirmed or not?
 * @param status
 * @returns
 */
export const isPaymentConfirmable = (status: PaymentIntentStatus | PaymentSetupIntentStatus) =>
    isPaymentIdle(status) || isPaymentPending(status);

export const cleanPspUrlSearchParams = (url: string, paymentProviderClient?: PaymentProviderClient) => {
    const nextUrl = new URL(url);
    const nextSearchParams = new URLSearchParams(nextUrl.search);

    if (paymentProviderClient === PaymentProviderClient.Stripe) {
        stripeCheckoutReturnParams.forEach((stripeParam) => {
            nextSearchParams.delete(stripeParam);
        });
    }

    // Remove all other params that are not in the list
    [
        "pending_booking_id", // set in CheckoutItemPage return url
    ].forEach((param) => nextSearchParams.delete(param));

    nextUrl.search = nextSearchParams.toString();

    return nextUrl.href;
};

/**
 * Helper function to get cleaned navigate target.
 * Just provide overrides e.g. { p: "base", c: "thank-you" } and it will return a cleaned target with the overrides.
 * @param overrides
 * @param paymentProviderClient
 * @returns
 */
export const getCleanedNavigateTarget = (overrides: { [key: string]: any }, paymentProviderClient?: PaymentProviderClient): string => {
    const currentHref = document.location.href;
    const currentSearchParams = UtilsUrl.parseSearchParamsByUrl(cleanPspUrlSearchParams(currentHref, paymentProviderClient));

    return `?${UtilsUrl.stringifySearchParams({
        ...currentSearchParams,
        ...overrides,
    })}`;
};

/**
 *
 * @param bookingId
 */
export const generateCheckoutReturnUrl = (bookingId: string) => {
    const returnUrl = new URL(document.location.href);
    const returnSearch = new URLSearchParams(returnUrl.search);
    const searchEntries = Array.from(returnSearch.entries());

    // we need to remove all ibe search params from the return url because we cannot
    // rely on the url params once returned from the payment provider. e.g. Stripe
    // will strip duplicate params (br in case of multi room booking).
    // searchEntries.forEach(([key, value]) => {
    //     if (appUtils.isIbeParam(key)) {
    //         returnSearch.delete(key);
    //     }
    // });

    returnSearch.set("p", "base");
    returnSearch.set("c", "checkout-return");
    returnSearch.set("pending_booking_id", bookingId);

    returnUrl.search = returnSearch.toString();

    return returnUrl;
};
